import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Divider,
    List,
    ListItem,
    IconButton,
    Collapse,
    ListItemText,
} from "@mui/material";
import { getEntityById, getEntityTriggers, getEntityIncidents, findEntitiesNear } from "../../api";
import DisplayIcon from "../Icon/DisplayIcon";
import { GlobalContext } from "../../GlobalProvider";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SensorsIcon from "@mui/icons-material/Sensors";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AlarmIcon from "@mui/icons-material/Alarm";
import FloatingChat from "../FloatingChat";
import Header from "../Header";
import SensorComponentRenderer from "../SensorDisplay/SensorComponentRenderer";

const EntityView = () => {
    const { id } = useParams();
    const { fullApp } = useContext(GlobalContext);
    const [entity, setEntity] = useState(null);
    const [sensorDetails, setSensorDetails] = useState({});
    const [triggers, setTriggers] = useState([]);
    const [incidents, setIncidents] = useState([]);
    const [aiContextData, setAIContextData] = useState("");
    const [nearestEntities, setNearestEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openSections, setOpenSections] = useState({
        incidents: true,
        nearestEntities: true,
        sensors: true,
        triggers: true,
    });
    const [sensorVisibility, setSensorVisibility] = useState({});

    useEffect(() => {
        fetchEntity();
    }, [id]);

    const fetchEntity = async () => {
        try {
            const response = await getEntityById(id);
            const entity = response.data;
            setEntity(entity);

            const details = {};
            entity.entityTypeId.sensors.forEach((sensor) => {
                details[sensor._id] = sensor;
            });
            setSensorDetails(details);
            fetchTriggers(entity.entityTypeId._id, entity._id);
            fetchIncidents(id);
            fetchNearestEntities(entity);
            setLoading(false);

        } catch (error) {
            console.error("Error fetching entity:", error.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!entity || !sensorDetails  || !triggers || !incidents || !nearestEntities) return;

        const aiContext = {
            entityName: entity.name,
            entityType: entity.entityTypeId?.name,
            appName: entity.appId?.name || "N/A",
            geoLocation: entity.isGeoLocated ? entity.keys.find(k => k.keyName === "location")?.value : "Not Geo-Located",
            sensors: entity.sensors.map(s => ({
                id: s._id,
                type: sensorDetails[s.sensorId]?.sensor_name || "Unknown",
            })),
            triggers: triggers.map(t => ({
                name: t.name,
                condition: t.conditionType,
                action: t.actionType
            })),
            incidents: incidents.map(i => ({
                timestamp: new Date(i.createdAt).toLocaleString(),
                description: i.description || "No details available",
            })),
            nearestEntities: nearestEntities.map(e => ({
                name: e.name,
                distance: e.distance || "Unknown"
            })),
        };

        setAIContextData(JSON.stringify(aiContext, null, 2));

    }, [entity, sensorDetails, triggers, incidents, nearestEntities]);




    const fetchTriggers = async (entityTypeId, id) => {
        let triggerList = [];
        try {
            const res = await getEntityTriggers(entityTypeId, id);
            triggerList = res.data.data;
        } catch (error) {
            console.error(`Error fetching trigger ${entityTypeId}:`, error.message);
        }
        setTriggers(triggerList);
    };

    const fetchIncidents = async (entityId) => {
        try {
            const res = await getEntityIncidents({ entityId });
            setIncidents(res.data.data);
        } catch (error) {
            console.error("Error fetching incidents:", error);
        }
    };

    const fetchNearestEntities = async (entity) => {
        try {
            const { latitude, longitude } = entity.keys.find((key) => key.keyName === "location")?.value || {};
            if (latitude && longitude) {
                const res = await findEntitiesNear({ latitude, longitude, maxDistance: 500 });
                setNearestEntities(res.data);
            }
        } catch (error) {
            console.error("Error fetching nearest entities:", error);
        }
    };


    const toggleSection = (section) => {
        setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const toggleSensorVisibility = (sensorId) => {
        setSensorVisibility((prev) => ({ ...prev, [sensorId]: !prev[sensorId] }));
    };


    if (loading) return <CircularProgress />;
    if (!entity) return <Typography variant="h6">Entity not found.</Typography>;


    return (
        <Box p={3}>
            <Header title={"Details : " + entity.name} />
            <FloatingChat data={aiContextData} />
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item>
                                    <DisplayIcon name={entity.entityTypeId.icon} />
                                </Grid>
                                <Grid item>
                                    <Typography variant="h5">{entity.name}</Typography>
                                    <Typography variant="subtitle1" color="textSecondary">
                                        Type: {entity.entityTypeId?.name}
                                    </Typography>
                                    <Typography variant="subtitle2">App: {entity.appId?.name}</Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6">Entity Properties</Typography>
                            <Divider sx={{ my: 1 }} />
                            <List>
                                {entity.keys.map((key) => (
                                    <ListItem key={key._id}>
                                        <ListItemText primary={`${key.keyName}: ${key.value}`} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Current Status */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6">Current Status</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body1">Geo-Located: {entity.isGeoLocated ? "Yes" : "No"}</Typography>
                    <Typography variant="body1">
                        Location: {entity.keys.find((key) => key.keyName === "latitude")?.value},{" "}
                        {entity.keys.find((key) => key.keyName === "longitude")?.value}
                    </Typography>
                </CardContent>
            </Card>

            {/* Recent Incidents */}
            <Card sx={{ mt: 3, p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" onClick={() => toggleSection("incidents")} sx={{ cursor: "pointer" }}>
                    <Typography variant="h6"><AlarmIcon sx={{ mr: 1 }} /> Recent Incidents</Typography>
                    <IconButton>{openSections.incidents ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </Box>
                <Collapse in={openSections.incidents}>
                    <Divider sx={{ my: 1 }} />
                    <List>
                        {incidents.length > 0 ? (
                            incidents.map((incident) => (
                                <ListItem key={incident._id}>
                                    <ListItemText primary={`Incident at ${new Date(incident.createdAt).toLocaleString()}`} />
                                </ListItem>
                            ))
                        ) : (
                            <Typography>No incidents recorded.</Typography>
                        )}
                    </List>
                </Collapse>
            </Card>

            {/* Nearest Entities */}
            <Card sx={{ mt: 3, p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" onClick={() => toggleSection("nearestEntities")} sx={{ cursor: "pointer" }}>
                    <Typography variant="h6"><LocationOnIcon sx={{ mr: 1 }} /> Nearby Entities</Typography>
                    <IconButton>{openSections.nearestEntities ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </Box>
                <Collapse in={openSections.nearestEntities}>
                    <Divider sx={{ my: 1 }} />
                    <List>
                        {nearestEntities.length > 0 ? (
                            nearestEntities.map((nearEntity) => (
                                <ListItem key={nearEntity._id}>
                                    <ListItemText primary={nearEntity.name} />
                                </ListItem>
                            ))
                        ) : (
                            <Typography>No nearby entities found.</Typography>
                        )}
                    </List>
                </Collapse>
            </Card>

            {/* Sensors Section */}
            <Card sx={{ mt: 3, p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" onClick={() => toggleSection("sensors")} sx={{ cursor: "pointer" }}>
                    <Typography variant="h6"><SensorsIcon sx={{ mr: 1 }} /> Streams</Typography>
                    <IconButton>{openSections.sensors ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </Box>
                <Collapse in={openSections.sensors}>
                    <Divider sx={{ my: 1 }} />
                    {entity.sensors.length > 0 ? (
                        <Grid container spacing={2}>
                            {entity.sensors.map((sensor) => {
                                const sensorInfo = sensorDetails[sensor.sensorId] || {};
                                const sensorType = sensorInfo.uiType || "unknown";
                                let SensorComponent = <SensorComponentRenderer uiType={sensorType}  sensorId= {sensor._id} />;

                                return (
                                    <Grid item xs={12} key={sensor.sensorId}>
                                        <Card>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                onClick={() => toggleSensorVisibility(sensor.sensorId)}
                                                sx={{ cursor: "pointer", p: 2 }}
                                            >
                                                <Box display="flex" alignItems="center">
                                                    <DisplayIcon name={sensorInfo.iconName} />
                                                    <Box ml={2}>
                                                        <Typography variant="h6">{sensor.externalId}</Typography>
                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Type: {sensorInfo.sensor_name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton>
                                                    {sensorVisibility[sensor.sensorId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Box>
                                            <Collapse in={sensorVisibility[sensor.sensorId]}>
                                                <Divider sx={{ my: 1 }} />
                                                <CardContent>
                                                    <Box>{SensorComponent}</Box>
                                                </CardContent>
                                            </Collapse>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ) : (
                        <Typography>No sensors available.</Typography>
                    )}
                </Collapse>
            </Card>

            {/* Triggers Section */}
            <Card sx={{ mt: 3, p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" onClick={() => toggleSection("triggers")} sx={{ cursor: "pointer" }}>
                    <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                        <AlarmIcon sx={{ mr: 1 }} /> Triggers Assoicated
                    </Typography>
                    <IconButton>{openSections.triggers ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </Box>
                <Collapse in={openSections.triggers}>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={2}>
                        {triggers.map((trigger) => (
                            <Grid item xs={12} sm={6} md={4} key={trigger._id}>
                                <Card sx={{ p: 2, textAlign: "center" }}>
                                    <CardContent>
                                        <Typography variant="h6">{trigger.name}</Typography>
                                        <Typography variant="body2" color="textSecondary">{trigger.description}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Collapse>
            </Card>
        </Box>
    );
};

export default EntityView;
